import React, { useState, FormEvent } from "react";
import { CreditCard, Clock, CheckCircle2, Send, Sparkles, AlertCircle } from "lucide-react";
import fondImageDesktop from "../../components/profileUser/assets/Fond-Large.jpg";
import fondImageMobile from "../../components/profileUser/assets/Fond.jpg";
import { comptesApi } from "../../api/clients.api";

const Wallet = () => {
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!email && !phone) return;

        setLoading(true);
        setError(null);

        try {
            await comptesApi.joinWaitlist({
                email: email || undefined,
                phone: phone || undefined,
                feature: 'carte',
            });
            setSubmitted(true);
        } catch (err: any) {
            const message = err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const features = [
        { emoji: "💳", title: "Carte Virtuelle", desc: "Payez en ligne instantanément" },
        { emoji: "🌍", title: "Internationale", desc: "Acceptée partout dans le monde" },
        { emoji: "🔒", title: "Sécurisée", desc: "Protection anti-fraude avancée" },
        { emoji: "📱", title: "Mobile First", desc: "Contrôle depuis votre téléphone" },
        { emoji: "💰", title: "Cashback", desc: "Gagnez à chaque achat" },
        { emoji: "⚡", title: "Instantanée", desc: "Activation en 2 minutes" },
    ];

    return (
        <div className="min-h-screen relative">
            {/* Background Images */}
            <div
                className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 md:hidden"
                style={{ backgroundImage: `url(${fondImageMobile})` }}
            />
            <div
                className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat -z-10 hidden md:block"
                style={{ backgroundImage: `url(${fondImageDesktop})` }}
            />

            <div className="px-6 md:px-16 lg:px-32 py-8">
                <div className="max-w-2xl mx-auto">
                    {/* Header Card */}
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 p-8 mb-8 shadow-2xl animate-fade-in">
                        {/* Decorative elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

                        <div className="relative z-10 flex flex-col items-center text-center">
                            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6">
                                <CreditCard className="w-10 h-10 text-white" />
                            </div>

                            <div className="flex items-center gap-2 bg-amber-400/20 text-amber-300 px-4 py-1.5 rounded-full text-xs font-bold mb-4">
                                <Clock className="w-4 h-4" />
                                Bientôt disponible
                            </div>

                            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                                Carte Bousanm
                            </h1>
                            <p className="text-blue-100 text-sm md:text-base max-w-md">
                                Votre carte virtuelle et physique pour des paiements sans friction,
                                partout en Haïti et dans le monde.
                            </p>
                        </div>
                    </div>

                    {/* Features Preview */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                        {features.map((feature, idx) => (
                            <div
                                key={idx}
                                className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-gray-100 hover:shadow-lg transition-shadow"
                            >
                                <span className="text-2xl mb-2 block">{feature.emoji}</span>
                                <h3 className="font-bold text-gray-900 text-sm mb-1">{feature.title}</h3>
                                <p className="text-xs text-gray-500">{feature.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Waitlist Form */}
                    <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 border border-gray-200 shadow-lg">
                        {submitted ? (
                            <div className="text-center py-8">
                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">
                                    Vous êtes sur la liste ! 🎉
                                </h3>
                                <p className="text-gray-500 text-sm">
                                    Nous vous contacterons dès que la carte sera disponible.
                                </p>
                            </div>
                        ) : (
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="w-5 h-5 text-amber-500" />
                                    <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">
                                        Accès prioritaire
                                    </span>
                                </div>

                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                                    Rejoindre la liste d'attente
                                </h2>
                                <p className="text-gray-500 text-sm mb-6">
                                    Soyez parmi les premiers à recevoir votre Carte Bousanm
                                </p>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="votre@email.com"
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                            Téléphone (optionnel)
                                        </label>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="+509 XXXX XXXX"
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm"
                                        />
                                    </div>

                                    {/* Error message */}
                                    {error && (
                                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading || (!email && !phone)}
                                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25 hover:from-blue-700 hover:to-blue-800 transition-all"
                                    >
                                        {loading ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4" />
                                                M'inscrire sur la liste
                                            </>
                                        )}
                                    </button>
                                </form>

                                <p className="text-center text-xs text-gray-400 mt-4">
                                    🔒 Vos informations sont sécurisées et ne seront jamais partagées.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Bottom spacer for mobile nav */}
                    <div className="h-24 md:hidden" />
                </div>
            </div>
        </div>
    );
};

export default Wallet;
